import {useCallback, useState} from 'react';
import {commitTicketingMutation} from '../../../graphql/forecastRelay';
import RegenerateTicketAiSummaryMutation from '../../../../../mutations/ticketing/tickets/regenerateTicketAiSummaryMutation';
import {extensionsFrom, gatewayMessageFrom, OUT_OF_CREDIT} from './useGenerateReplyDraft';

interface Options {
	onCompleted?: () => void;
	onError?: (error: Error) => void;
}

// ACC-297: triggers a synchronous regenerate. Unlike useDismissSuggestedAction we
// also surface `error` so the blocks can render the friendly rate-limit / failure
// message (AC #6) — the backend's 429 message propagates through the mutation.
// An out-of-shared-AI-credit refusal is not a failure: its gateway copy is exposed
// as `notice`, to be shown as ordinary content, and `error` stays null.
export function useRegenerateAiSummary(options?: Options) {
	const [inFlight, setInFlight] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const [notice, setNotice] = useState<string | null>(null);

	const regenerate = useCallback(
		(ticketId: string) => {
			setInFlight(true);
			setError(null);
			setNotice(null);
			commitTicketingMutation(
				RegenerateTicketAiSummaryMutation,
				{ticketId},
				() => {
					setInFlight(false);
					options?.onCompleted?.();
				},
				err => {
					const e = err instanceof Error ? err : new Error(String(err));
					const gatewayMessage = gatewayMessageFrom(err);
					setInFlight(false);
					if (extensionsFrom(err)?.ticketingCode === OUT_OF_CREDIT && gatewayMessage) {
						setNotice(gatewayMessage);
					} else {
						setError(e);
					}
					options?.onError?.(e);
				}
			);
		},
		[options]
	);

	return {regenerate, inFlight, error, notice};
}
